import AddIcon from '@/components/icons/AddIcon';
import { Button } from '@/components/ui/button';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TrusteeElement from '@/features/trustees/components/TrusteesListElement';
import { TrusteeStatus } from '@/features/trustees/types/trustees-dto';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '@/lib/utils';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { Roles } from '@/@types/auth.types.ts';

const testData: {
	name: string;
	type: 'local' | 'foreign';
	status: TrusteeStatus;
}[] = [
	{ name: 'Trustee 1', type: 'local', status: 0 },
	{ name: 'Trustee 2', type: 'foreign', status: 1 },
	{ name: 'Trustee 3', type: 'local', status: 0 },
	{ name: 'Trustee 4', type: 'foreign', status: 2 },
	{ name: 'Trustee 5', type: 'foreign', status: 3 },
	{ name: 'Trustee 6', type: 'local', status: 4 },
	{ name: 'Trustee 7', type: 'foreign', status: 4 },
];

type Props = {
	isMobile?: boolean;
};

const TrusteesList: FC<Props> = ({ isMobile }) => {
	const [activeList, setActiveList] = useState(TrusteeStatus.Active);
	const { t } = useTranslation();

	const userRole = useUserRole();

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'center',
		dragFree: true,
	});

	const clickHandler = (index: number) => {
		setActiveList(index);
		if (emblaApi) {
			emblaApi.scrollTo(index);
		}
	};

	const trusteesButtonsList = Object.values(TrusteeStatus).filter(
		(value) => typeof value === 'string',
	);

	const filteredList = testData.filter(
		(trustee) => trustee.status === activeList,
	);

	return (
		<div className='flex flex-col gap-4 select-none'>
			<div className='flex items-center justify-between gap-2'>
				<h3 className={cn(isMobile && 'text-base')}>
					{t('main.trustees.title')}
				</h3>
				{userRole === Roles.owner && (
					<Button variant='outline'>
						<AddIcon />
						{t('elements.buttons.newTrustee')}
					</Button>
				)}
			</div>
			<div
				className={cn(
					'-mx-6 px-6 overflow-hidden',
					isMobile && '-mx-4 px-4',
				)}
				ref={emblaRef}>
				<div className='flex gap-2 touch-pan-y'>
					{trusteesButtonsList.map((button, index) => (
						<Button
							key={index}
							variant={
								activeList === index ? 'default' : 'outline'
							}
							className={`hover:shadow-none px-3.5 py-2 ${
								activeList === index
									? ''
									: 'hover:bg-popover-50'
							} `}
							onClick={() => clickHandler(index)}>
							{button}
						</Button>
					))}
				</div>
			</div>
			<div className='flex flex-col gap-2'>
				{filteredList.map((agr, index) => (
					<TrusteeElement
						key={index}
						{...agr}
						isMobile={isMobile}
					/>
				))}
			</div>
		</div>
	);
};

export default TrusteesList;
